import React, { useState, useEffect } from 'react';
import { X, Shield, FileText, Clock, Plus } from 'lucide-react';
import { useRecentEntities } from '../contexts/RecentEntitiesContext';
import { RecentEntity } from '../types/RecentEntities';
import {
  AdminClientApi,
  ClientBelief,
  ClientThoughtWithAuthor,
  AddContentToFeedInputInterface,
} from '../Admin-ClientLibrary';
import { getApiUrl } from '../config/api';
import './AddContentToFeedModal.css';

const getToken = (): string => {
  return localStorage.getItem('authToken') || '';
};

const adminApi = new AdminClientApi(getApiUrl(), getToken);

interface AddContentToFeedModalProps {
  isOpen: boolean;
  onClose: () => void;
  feedId: number;
  onSuccess: (feedRank: number) => void;
}

const AddContentToFeedModal: React.FC<AddContentToFeedModalProps> = ({
  isOpen,
  onClose,
  feedId,
  onSuccess,
}) => {
  const { recentEntities } = useRecentEntities();
  const [filterType, setFilterType] = useState<'belief' | 'thought'>('belief');
  const [selectedEntity, setSelectedEntity] = useState<RecentEntity | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedEntity(null);
      setError(null);
    }
  }, [isOpen]);

  // Get filtered recent entities based on selected type
  const filteredEntities = recentEntities.filter((entity) => entity.type === filterType);

  const handleEntitySelect = (entity: RecentEntity) => {
    setSelectedEntity(entity);
  };

  const handleSave = async () => {
    if (!selectedEntity) {
      setError('Please select an item to add');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const apiParams: AddContentToFeedInputInterface = {
        feedId,
        contentType: filterType,
        contentId: parseInt(selectedEntity.id),
      };

      const response = await adminApi.addContentToFeed(apiParams);

      if (response.success) {
        onSuccess(response.feedRank || 1);
        onClose();
      } else {
        setError(response.message || 'Failed to add content to feed');
      }
    } catch (err: any) {
      console.error('Error adding content to feed:', err);
      setError(err.message || 'Network error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const getEntityDisplayText = (entity: RecentEntity): string => {
    switch (entity.type) {
      case 'belief':
        const belief = entity.data as ClientBelief;
        return `Belief ${entity.id} (${belief.position?.toUpperCase() || 'UNKNOWN'})`;
      case 'thought':
        const thought = entity.data as ClientThoughtWithAuthor;
        const text = thought.text || '';
        return text.length > 80 ? `${text.substring(0, 80)}...` : text;
      default:
        return `Entity ${entity.id}`;
    }
  };

  const getEntityIcon = (type: string) => {
    switch (type) {
      case 'belief':
        return Shield;
      case 'thought':
        return FileText;
      default:
        return FileText;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content add-content-modal">
        <div className="modal-header">
          <h3>Add Content from Recent</h3>
          <button className="close-button" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          {/* Filter Type Selector */}
          <div className="filter-section">
            <label htmlFor="content-type">Content Type:</label>
            <select
              id="content-type"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as 'belief' | 'thought')}
              className="type-selector"
            >
              <option value="belief">Beliefs</option>
              <option value="thought">Thoughts</option>
            </select>
          </div>

          {/* Recent Items List */}
          <div className="recent-items-section">
            <h4>
              <Clock size={16} />
              Recent {filterType === 'belief' ? 'Beliefs' : 'Thoughts'}
            </h4>

            {filteredEntities.length === 0 ? (
              <div className="no-items">
                <p>No recent {filterType === 'belief' ? 'beliefs' : 'thoughts'} found.</p>
                <p>
                  Visit some {filterType === 'belief' ? 'beliefs' : 'thoughts'} first to see them
                  here.
                </p>
              </div>
            ) : (
              <div className="items-list">
                {filteredEntities.map((entity) => {
                  const Icon = getEntityIcon(entity.type);
                  const isSelected =
                    selectedEntity?.id === entity.id && selectedEntity?.type === entity.type;

                  return (
                    <div
                      key={`${entity.type}-${entity.id}`}
                      className={`item-card ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleEntitySelect(entity)}
                    >
                      <div className="item-icon">
                        <Icon size={16} />
                      </div>
                      <div className="item-content">
                        <div className="item-id">ID: {entity.id}</div>
                        <div className="item-text">{getEntityDisplayText(entity)}</div>
                      </div>
                      {isSelected && (
                        <div className="selected-indicator">
                          <Plus size={16} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="error-message">
              <p>{error}</p>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={handleCancel} disabled={isLoading}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={handleSave}
            disabled={isLoading || !selectedEntity}
          >
            {isLoading ? 'Adding...' : 'Add to Feed'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddContentToFeedModal;
